import { createLogger } from '@svenskaspelab/nova-core/log';
import type { trackEcommerce, trackEvent } from '@svenskaspelab/nova-core/nextjs/analytics';
import type { DialogConfig } from '@svenskaspelab/nova-foundation/nextjs/dialog';

import { type CouponId, type CouponIndex, getCouponIndex } from '../../../../schema/coupon/entry.js';
import { getInstanceClient } from '../../../di/entry.browser.js';
import {
  CouponEntity,
  NotInitializedError,
  PoolbettingError,
  type SelfServicePaymentContext,
} from '../../../domain/entry.js';
import type { ActionResult } from '../../../lib/entry.browser.js';

const logger = createLogger('poolbetting.place-selfservice-coupon-action');

type PlaceSelfServiceCouponArgs = {
  couponId: CouponId;
  showDialog: (props: DialogConfig, meta?: string[]) => Promise<void>;
};

type PlaceSelfServiceCouponReturn = ActionResult<{ addedCouponsCount: number }>;

type SelfServicePaymentContextArgs = {
  selfService: SelfServicePaymentContext;
  trackTransaction: typeof trackEcommerce;
  trackEventFunction: typeof trackEvent;
};

/**
 * Adds valid coupons for a given couponId to the self-service cart.
 *
 * Unlike {@link import('./placeCouponWager.js').placeCouponWager} this flow never charges the player,
 * so it is independent of user role, wallets and account balance.
 *
 * @param root0 Place coupon args
 * @param root0.couponId Unique coupon id to add to the self-service cart
 * @param root0.showDialog Dialog function, used by the shared wager controller
 * @param paymentContext Self-service cart context and analytics functions
 * @returns status: SUCCESS with the number of added coupons, otherwise status: ERROR with error message
 */
export async function placeSelfServiceCouponWager(
  { couponId, showDialog }: PlaceSelfServiceCouponArgs,
  paymentContext: SelfServicePaymentContextArgs
): Promise<PlaceSelfServiceCouponReturn> {
  const getCouponsController = getInstanceClient('IGetCouponsController');
  const createWagerController = getInstanceClient('ICreateWagerController');
  const definitionService = getInstanceClient('IDefinitionService');
  const resetCouponController = getInstanceClient('IResetCouponController');
  const updateCouponController = getInstanceClient('IUpdateCouponController');

  const [getCoupons] = getCouponsController({ couponId });

  try {
    const coupons = getCoupons();
    const [firstCoupon] = coupons;

    if (!firstCoupon) {
      return { status: 'ERROR', error: 'Det finns inget att lägga i kundvagnen' };
    }

    const definition = await definitionService.resolveDefinitionAsync(firstCoupon.product);

    if (!definition) {
      throw new NotInitializedError(`Unable to load definition for product: ${firstCoupon.product}`);
    }

    const validCouponIndices: CouponIndex[] = [];
    for (const [couponIndex, coupon] of coupons.entries()) {
      const isValid = CouponEntity.getStatus(coupon, { definition: definition.model }) === 'VALID';
      if (isValid && getCouponIndex(couponIndex)) {
        validCouponIndices.push(couponIndex);
      }
    }

    const hasInvalidCouponIndices = validCouponIndices.length !== coupons.length;

    const wagerResponse = await createWagerController({
      couponId,
      couponIndices: validCouponIndices,
      wallets: undefined,
      showDialog,
      paymentContext,
      hasInvalidCouponIndices,
      couponType: firstCoupon.couponType,
    });

    if (wagerResponse.unpaidItemsCount === 0 && !hasInvalidCouponIndices && validCouponIndices[0] !== undefined) {
      await resetCouponController({ couponId, couponIndex: validCouponIndices[0] });
      await updateCouponController(
        { betAmount: definition.betAmount },
        { couponId, couponIndex: validCouponIndices[0] }
      );
    }

    const addedCouponsCount = wagerResponse.paidItemsCount;

    if (addedCouponsCount > 0) {
      paymentContext.trackEventFunction({
        category: 'Nya tipsen',
        action: 'Tillagd i kundvagn',
        optLabel: `Antal kuponger: ${addedCouponsCount}`,
      });
    }

    return { status: 'SUCCESS', data: { addedCouponsCount } };
  } catch (error) {
    if (PoolbettingError.isInstance(error)) {
      return error.toActionResult();
    }

    logger.error('Failed to add coupon to self-service cart', { error, couponId });

    return {
      status: 'ERROR',
      error: 'Något gick fel när kupongen skulle läggas i kundvagnen',
      reason: error instanceof Error && typeof error.cause === 'string' ? error.cause : undefined,
    };
  }
}
